import React, { useEffect, useState } from 'react';
import ImpactDropdown from './ImpactDropdown';
import ComplexityDropdown from './ComplexityDropdown';
import { calculateScore, getWeightedImpact, getComplexityScore } from '../../utils/scoreCalculator';
import { categories } from '../../utils/constants';
import { rewriteProcessSummary, rescoreProcess } from '../../services/aiService';

const ProcessRow = React.memo(function ProcessRow({ process, onProcessChange, contextData }) {
  const [weightedScore, setWeightedScore] = useState('0.00');
  const [weightedImpact, setWeightedImpact] = useState('None');
  const [complexityScore, setComplexityScore] = useState('0');
  const [rewritingSummary, setRewritingSummary] = useState(false);
  const [rescoring, setRescoring] = useState(false);

  // Recalculate scores whenever process changes
  useEffect(() => {
    const score = calculateScore(process);
    setWeightedScore(score);
    setWeightedImpact(getWeightedImpact(score));
    setComplexityScore(getComplexityScore(process.complexity).toString());
  }, [process]);

  const handleChange = (field, value) => {
    onProcessChange({
      ...process,
      [field]: value
    });
  };

  const handleRewriteSummary = async () => {
    if (!process.name || !process.summary) {
      return; // Need both name and summary to rewrite
    }

    setRewritingSummary(true);
    try {
      const result = await rewriteProcessSummary(
        process.name,
        process.summary,
        contextData
      );
      handleChange('summary', result.summary);
    } catch (error) {
      console.error('Failed to rewrite summary:', error);
    } finally {
      setRewritingSummary(false);
    }
  };

  const handleRescore = async () => {
    if (!process.name) {
      return; // Need at least a name to rescore
    }

    setRescoring(true);
    try {
      const result = await rescoreProcess(process, contextData);

      // Update process with new ratings
      onProcessChange({
        ...process,
        ...result.ratings
      });
    } catch (error) {
      console.error('Failed to rescore process:', error);
    } finally {
      setRescoring(false);
    }
  };

  return (
    <>
      {/* Impact Row */}
      <tr className="border-b hover:bg-gray-50">
        <td className="px-2 py-2 text-xs text-gray-600">
          <div className="flex items-center gap-1">
            <span>{process.id}</span>
            <button
              onClick={handleRescore}
              disabled={rescoring || !process.name}
              title="Rescore this process with AI"
              className="px-1 py-1 text-xs bg-purple-50 hover:bg-purple-100 disabled:bg-gray-100 disabled:text-gray-400 border border-purple-200 rounded transition-colors"
              style={{ minWidth: '20px', fontSize: '10px' }}
            >
              {rescoring ? '...' : '✨'}
            </button>
          </div>
        </td>
        <td className="px-2 py-2 text-sm">
          <textarea
            value={process.name || ''}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="Process name..."
            rows={2}
            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 resize-vertical"
          />
        </td>
        <td className="px-2 py-2 text-xs text-gray-600">
          <div className="flex items-start gap-1">
            <textarea
              value={process.summary || ''}
              onChange={(e) => handleChange('summary', e.target.value)}
              placeholder="Summary..."
              rows={2}
              className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 resize-vertical"
            />
            <button
              onClick={handleRewriteSummary}
              disabled={rewritingSummary || !process.name || !process.summary}
              title="Rewrite summary with AI"
              className="px-1 py-1 text-xs bg-blue-50 hover:bg-blue-100 disabled:bg-gray-100 disabled:text-gray-400 border border-blue-200 rounded transition-colors"
              style={{ minWidth: '20px', fontSize: '10px' }}
            >
              {rewritingSummary ? '...' : '✨'}
            </button>
          </div>
        </td>
        <td className="px-2 py-2">
          <ComplexityDropdown
            id={`${process.id}_complexity`}
            name="complexity"
            value={process.complexity}
            onChange={(e) => handleChange('complexity', e.target.value)}
          />
        </td>
        <td className="px-2 py-2 text-center text-sm font-medium">{complexityScore}</td>
        <td className="px-2 py-2 text-center bg-blue-50 font-medium">Impact</td>

        {/* Impact Dropdowns for each category */}
        {categories.map((cat) => (
          <td key={`impact_${cat.key}`} className="px-2 py-2">
            <ImpactDropdown
              id={`${process.id}_impact_${cat.key}`}
              name={`impact_${cat.key}`}
              value={process[`impact_${cat.key}`]}
              onChange={(e) => handleChange(`impact_${cat.key}`, e.target.value)}
            />
          </td>
        ))}

        <td className="px-2 py-2 text-center text-sm font-bold bg-green-50">
          {weightedScore}
        </td>
        <td className="px-2 py-2 text-center text-sm bg-yellow-50">
          {weightedImpact}
        </td>
      </tr>

      {/* Importance Row */}
      <tr className="border-b hover:bg-gray-50 bg-gray-50">
        <td className="px-2 py-2"></td>
        <td className="px-2 py-2"></td>
        <td className="px-2 py-2"></td>
        <td className="px-2 py-2"></td>
        <td className="px-2 py-2"></td>
        <td className="px-2 py-2 text-center bg-purple-50 font-medium">Importance</td>

        {/* Importance Dropdowns for each category */}
        {categories.map((cat) => (
          <td key={`importance_${cat.key}`} className="px-2 py-2">
            <ImpactDropdown
              id={`${process.id}_importance_${cat.key}`}
              name={`importance_${cat.key}`}
              value={process[`importance_${cat.key}`]}
              onChange={(e) => handleChange(`importance_${cat.key}`, e.target.value)}
            />
          </td>
        ))}

        <td className="px-2 py-2"></td>
        <td className="px-2 py-2"></td>
      </tr>
    </>
  );
});

export default ProcessRow;
